import { DestroyRef, Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, filter, Observable, Subject, switchMap, tap } from 'rxjs';
import { MercerSupportDetailsComponentTab } from '../enums/mercer-support-details-tabs.enum';
import { MercerSupportHelper } from '../helpers/mercer-support.helper';
import { MercerDialogType } from '../enums/mercer-dialog-type.enum';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails
} from '../models/mercer-support-evaluation-member.model';
import { PermissionsService } from '@coin/shared/auth/feature';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface CurrentTabInformation {
  currentTab: MercerSupportDetailsComponentTab;
  previousTab?: MercerSupportDetailsComponentTab;
}

@Injectable()
export class MercerSupportDetailsService {
  private store = inject(Store);
  private permissionsService = inject(PermissionsService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  private readonly currentTabSubject$ = new BehaviorSubject<CurrentTabInformation>(null);
  private readonly loadMemberDetailsSubject$ = new Subject<void>();
  public readonly currentTab$ = this.currentTabSubject$.asObservable();
  public readonly loadMemberDetails$ = this.loadMemberDetailsSubject$.asObservable();

  public evaluationMember?: MercerEvaluationMemberDetails;
  public dialogType: MercerDialogType;

  public get isReadOnly(): boolean {
    const { evaluationMember } = this;
    return (
      MercerSupportHelper.isReadOnly(evaluationMember, this.dialogType, this.store) ||
      MercerSupportHelper.isLockedNewHire(evaluationMember)
    );
  }

  public get canLock(): boolean {
    return MercerSupportHelper.canLock(this.store, this.evaluationMember, this.dialogType, this.permissionsService);
  }

  public get canUnlock(): boolean {
    return MercerSupportHelper.canUnlock(this.store, this.evaluationMember, this.dialogType, this.permissionsService);
  }

  public get canReviewNewHire(): boolean {
    return MercerSupportHelper.canReviewNewHire(this.store, this.evaluationMember, this.dialogType);
  }

  public get canPromote(): boolean {
    return MercerSupportHelper.canPromote(this.store, this.evaluationMember, this.dialogType);
  }

  public get canUndoPromotion(): boolean {
    return MercerSupportHelper.canUndoPromotion(
      this.store,
      this.evaluationMember,
      this.dialogType,
      this.permissionsService
    );
  }

  public canChangeValidationManager(member: MercerEvaluationMember): boolean {
    return (
      this.dialogType === MercerDialogType.Support &&
      !this.isReadOnly &&
      MercerSupportHelper.isAdmin(this.permissionsService) &&
      !member.isHead
    );
  }

  // TODO: this is actually just used to start a new survey when the "Start Survey" button is used to change tabs from JobProfile -> Survey
  // should be named accordingly
  public updateTabInfo(tabInfo: CurrentTabInformation): void {
    this.currentTabSubject$.next(tabInfo);
  }

  public loadMemberDetails(): void {
    this.loadMemberDetailsSubject$.next();
  }

  public lock(): Observable<unknown> {
    return MercerSupportHelper.lock(this.store, this.evaluationMember.id);
  }

  public unlock(): Observable<unknown> {
    return MercerSupportHelper.unlock(this.store, this.evaluationMember.id);
  }

  public promote(): void {
    ConfirmationDialogComponent.openDialog(this.matDialog, {
      translate: true,
      msg: 'mercer.promote-dialog-message',
      headline: 'mercer.promote-dialog-headline',
      confirmMsg: 'general.btnConfirm',
      cancelMsg: 'general.btnCancel'
    })
      .pipe(
        filter(confirm => !!confirm),
        switchMap(() => MercerSupportHelper.promote(this.dialogType, this.store, this.evaluationMember.id)),
        tap(() => this.loadMemberDetailsSubject$.next()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public undoPromotion() {
    ConfirmationDialogComponent.openDialog(this.matDialog, {
      translate: true,
      msg: 'mercer.undo-promote-dialog-message',
      headline: 'mercer.undo-promote-dialog-headline',
      confirmMsg: 'general.btnConfirm',
      cancelMsg: 'general.btnCancel'
    })
      .pipe(
        filter(confirm => !!confirm),
        switchMap(() => MercerSupportHelper.undoPromote(this.dialogType, this.store, this.evaluationMember.id)),
        tap(() => this.loadMemberDetailsSubject$.next()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
